import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import fetch from 'unfetch';
import { feature as topo2geo } from 'topojson-client';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';

import {
  scrollMapIntoViewport, getColorizer, getColorsBySteps, getColorsByData
} from '~/core/scripts/map-components/utils';
import BasisMap from '~/core/scripts/map-components/BasisMap';
import Choropleth from '~/core/scripts/map-components/ChoroplethLayer';
import Overlay from '~/core/scripts/map-components/OverlayLayer';
import GeocoderMarker from '~/core/scripts/map-components/GeocoderMarker'

import { is768Query } from '~/core/scripts/mediaquery-helper';
import Geocoder from '~/core/scripts/components/Geocoder';
import Legend from '~/core/scripts/components/Legend';
import Tooltip from '~/core/scripts/components/Tooltip';
import Portal from '~/core/scripts/components/Portal';

import './Map.styl';

function propError(propName, componentName) {
  return new Error(
    `Invalid prop ${propName} supplied to ${componentName}.
    You need to pass choroplethSource OR choroplethData.`
  );
}

class MapWrapper extends PureComponent {
  static propTypes = {
    choroplethSource: (props, propName, componentName) => {
      if (!props.choroplethData) {
        if (typeof props[propName] !== 'string') {
          propError(propName, componentName);
        }
      }
    },
    choroplethData: (props, propName, componentName) => {
      if (!props.choroplethSource) {
        if (typeof props[propName] !== 'object') {
          propError(propName, componentName);
        }
      }
    },
    colorKey: PropTypes.string,
    valueKey: PropTypes.string,
    colorSteps: PropTypes.arrayOf(PropTypes.array),
    showLegend: PropTypes.bool,
    legendPortalSelector: PropTypes.string,
    showMapToggleButton: PropTypes.bool,
    zoomDelta: PropTypes.number,
    zoomSnap: PropTypes.number,
    zoomAnimation: PropTypes.bool,
    isFullScreen: PropTypes.bool,
    searchPresets: PropTypes.array,
    searchPresetsTitle: PropTypes.string,
    smoothFactor: PropTypes.number
  }

  static defaultProps = {
    colorKey: null,
    valueKey: null,
    colorSteps: null,
    showGeocoder: false,
    showLegend: false,
    legendPortalSelector: null,
    showMapToggleButton: true,
    zoomDelta: 1,
    zoomSnap: 0.2,
    zoomAnimation: true,
    isFullScreen: false,
    searchPresets: [],
    smoothFactor: 0.5
  }

  state = {
    data: null,
    mapBounds: null,
    tooltipPos: { x: 0, y: 0 },
    tooltipData: null,
    tooltipVisible: false,
    geocoderLatLng: [],
    overlayGeoJson: null,
    maxBounds: null
  }

  constructor(props) {
    super();

    this.colorizer = getColorizer(props.colorKey, props.valueKey, props.colorSteps);
  }

  componentDidMount() {
    this.loadData();
  }

  componentWillReceiveProps(props) {
    this.colorizer = getColorizer(props.colorKey, props.valueKey, props.colorSteps);
  }

  loadData = async () => {
    const topojson = await this.getTopojson();
    const key = Object.keys(topojson.objects)[0];
    const data = topo2geo(topojson, topojson.objects[key]);
    const mapBounds = L.geoJSON(data).getBounds();
    const maxBounds = mapBounds.pad(0.2);

    if (!this.props.overlaySource) {
      return this.setState({ data, mapBounds, maxBounds });
    }

    let overlayGeoJson = null;

    if (this.props.overlaySource === this.props.choroplethSource) {
      overlayGeoJson = topojson;
    } else {
      const overlayResponse = await fetch(this.props.overlaySource);
      overlayGeoJson = await overlayResponse.json();
    }

    this.setState({ overlayGeoJson, data, mapBounds, maxBounds });
  }

  getTopojson = async () => {
    if (this.props.choroplethSource) {
      const response = await fetch(this.props.choroplethSource);
      const topojson = await response.json();
      return topojson;
    }

    return this.props.choroplethData;
  }

  onCloseTooltip = () => {
    this.setState({ tooltipVisible: false });
  }

  onGeocoderSuccess = (res) => {
    const latlng = res.response.view[0].result[0].location.displayPosition;
    this.setState({
      geocoderLatLng: [
        latlng.latitude + Math.random() / 10000,
        latlng.longitude + Math.random() / 10000
      ]
    });

    if (!is768Query()) {
      scrollMapIntoViewport();

      // for mobile devices we want to show the tooltip
      let matchingFeature = null;

      for (let i = 0; i < this.state.data.features.length; i++) {
        const currFeat = this.state.data.features[i];
        const isInside = currFeat.type && currFeat.geometry && booleanPointInPolygon([latlng.longitude, latlng.latitude], currFeat);
        if (isInside) {
          matchingFeature = currFeat;
          break;
        }
      }

      this.onMouseEnter(matchingFeature.properties);
    }
  }

  onMouseEnter = (tooltipData) => {
    this.setState({
      tooltipVisible: true,
      tooltipData: {
        value: tooltipData[this.props.valueKey],
        title: tooltipData.plz,
        subtitle: tooltipData.ortsteile,
        color: this.colorizer(tooltipData)
      }
    });
  }

  onMouseMove = (tooltipPos) => {
    this.setState({ tooltipPos });
  }

  onMouseOut = () => {
    this.setState({ tooltipVisible: false });
  }

  renderLegend() {
    if (!this.props.showLegend) {
      return null;
    }

    const legendColors = this.props.colorSteps ?
      getColorsBySteps(this.props.colorSteps) :
      getColorsByData({ data: this.state.data, colorKey: this.props.colorKey, valueKey: this.props.valueKey });

    const LegendComp = (
      <Legend
        isVisible={!!this.state.data}
        minLabel={this.props.legendMinLabel}
        maxLabel={this.props.legendMaxLabel}
        colors={legendColors}
      />
    );

    if (this.props.legendPortalSelector) {
      return (
        <Portal selector={this.props.legendPortalSelector}>
          {LegendComp}
        </Portal>
      );
    }

    return LegendComp;
  }

  render() {
    return (
      <div className="map-widget">
        {this.renderLegend()}
        <div className="map-wrapper">
          <BasisMap
            minZoom={this.props.minZoom}
            maxZoom={this.props.maxZoom}
            maxBounds={this.state.maxBounds}
            fitBounds={this.state.mapBounds}
            showBasemap={this.props.showBasemap}
            basemapUrl={this.props.basemapUrl}
            basemapAttribution={this.props.basemapAttribution}
            geocoderLatLng={this.state.geocoderLatLng}
            zoomControl={this.props.zoomControl}
            showMapToggleButton={this.props.showMapToggleButton}
            showLabels={this.props.showLabels}
            labelPreset={this.props.labelPreset}
            customLabels={this.props.customLabels}
            zoomDelta={this.props.zoomDelta}
            zoomSnap={this.props.zoomSnap}
            zoomAnimation={this.props.zoomAnimation}
            isFullScreen={this.props.isFullScreen}
          >
            <Choropleth
              data={this.state.data}
              colorizer={this.colorizer}
              onMouseEnter={this.onMouseEnter}
              onMouseMove={this.onMouseMove}
              onMouseOut={this.onMouseOut}
              className="mapwidget__choropleth"
              smoothFactor={this.props.smoothFactor}
            />
            <Overlay
              data={this.state.overlayGeoJson}
              dissolveKey={this.props.overlayDissolveKey}
              className="mapwidget__overlay"
              smoothFactor={this.props.smoothFactor}
            />
            <GeocoderMarker
              position={this.state.geocoderLatLng}
            />
          </BasisMap>
          <Tooltip
            isVisible={this.state.tooltipVisible}
            pos={this.state.tooltipPos}
            data={this.state.tooltipData}
            onClose={this.onCloseTooltip}
          />
        </div>
        {this.props.showGeocoder && (
          <Geocoder
            title="Wie ist es bei Ihnen?"
            onSelect={this.onGeocoderSuccess}
            presets={this.props.searchPresets}
            presetsTitle={this.props.searchPresetsTitle}
          />
        )}
      </div>
    );
  }
}

export default MapWrapper;
